/**
 * The Service side's own frame.
 *
 * A twin of components/layout/AppShell, not a reuse of it. The two sides have
 * different navigation, different colour and different vocabulary, and the
 * moment one shell serves both it grows a `mode` prop and every change to
 * either side has to be reasoned about twice.
 *
 * Which side you are on must be clear at a glance - an engineer handed
 * somebody's phone should know in a second. Until 30-09-2026 that was amber
 * running down this side. The Board's branding (Branding-Colors.docx) retires
 * amber, so it is now said by the SERVICE tag in the sidebar and a thin
 * emerald rule under the header, which the Materials side does not have.
 */
import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import {
  Banknote, ClipboardCheck, FileSignature, Gauge, HardHat, Landmark, Library,
  HandCoins, ListChecks, LogOut, Menu, Receipt, Ruler, Scale, ShieldCheck, Wallet, X, PiggyBank, Scissors,
  SlidersHorizontal, Truck,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { BUILD_LABEL } from '@/lib/build'
import { isReadOnly, ROLE_LABEL } from '@/lib/domain'
import { ModuleSwitch } from '@/components/layout/ModuleSwitch'
import { NotificationBell } from '@/components/layout/NotificationBell'
import { ChangePasswordButton } from '@/components/layout/ChangePassword'
import { MobileTabBar, MobileTabSpacer, pickMobileTabs, type MobileTab } from '@/components/layout/MobileTabBar'
import { canSeeServiceRoute } from './nav'
import type { UserRole } from '@/lib/types'

interface Entry { to: string; label: string; icon: typeof Gauge; group: string; end?: boolean }

const NAV: Entry[] = [
  { to: '/service', label: 'Dashboard', icon: Gauge, group: 'Overview', end: true },

  { to: '/service/work-orders', label: 'Work Orders', icon: FileSignature, group: 'Contracts' },
  { to: '/service/variations', label: 'Variation Orders', icon: ListChecks, group: 'Contracts' },
  { to: '/service/work-items', label: 'Schedule of Rates', icon: HardHat, group: 'Contracts' },

  { to: '/service/measurements', label: 'Measurement Book', icon: Ruler, group: 'At Site' },
  // 0214: machines on hire, day by day.
  { to: '/service/hire-logs', label: 'Hire Log Sheets', icon: Truck, group: 'At Site' },

  { to: '/service/ra-bills', label: 'RA Bills', icon: Receipt, group: 'Money' },
  { to: '/service/contractor-payables', label: 'Contractor Payables', icon: Wallet, group: 'Money' },
  { to: '/service/payments', label: 'Payments', icon: Banknote, group: 'Money' },
  { to: '/service/statutory', label: 'Statutory & Deductions', icon: Landmark, group: 'Money' },
  { to: '/service/advances', label: 'Advances', icon: HandCoins, group: 'Money' },
  { to: '/service/credit-notes', label: 'Credit Notes', icon: Scissors, group: 'Money' },
  { to: '/service/reverse-charge', label: 'Reverse Charge', icon: Scale, group: 'Money' },
  { to: '/service/retention-releases', label: 'Retention Releases', icon: PiggyBank, group: 'Money' },

  // Last, because it is looked up rather than worked in. Contractors, sites
  // and units — read only; they are maintained on the Materials side.
  { to: '/service/masters', label: 'Masters', icon: Library, group: 'Reference' },
  // 29-09-2026, migration 0184. The System Administrator alone.
  { to: '/service/settings', label: 'Settings', icon: SlidersHorizontal, group: 'Reference' },
]

/**
 * The phone's bottom bar on the Service side (13-10-2026). Until then this
 * side had none. Same rules as the Materials bar: only screens the role may
 * open, topped up from SERVICE_FALLBACK. See components/layout/MobileTabBar.tsx.
 */
const MOBILE_CATALOGUE: Record<string, MobileTab> = {
  '/service': { to: '/service', label: 'Home', icon: Gauge, end: true },
  '/service/work-orders': { to: '/service/work-orders', label: 'Work orders', icon: FileSignature },
  '/service/variations': { to: '/service/variations', label: 'Variations', icon: ListChecks },
  '/service/work-items': { to: '/service/work-items', label: 'Rates', icon: HardHat },
  '/service/measurements': { to: '/service/measurements', label: 'Measure', icon: Ruler },
  '/service/ra-bills': { to: '/service/ra-bills', label: 'RA bills', icon: Receipt },
  '/service/contractor-payables': { to: '/service/contractor-payables', label: 'Payables', icon: Wallet },
  '/service/payments': { to: '/service/payments', label: 'Payments', icon: Banknote },
  '/service/statutory': { to: '/service/statutory', label: 'Statutory', icon: Landmark },
  '/service/advances': { to: '/service/advances', label: 'Advances', icon: HandCoins },
  '/service/masters': { to: '/service/masters', label: 'Masters', icon: Library },
}

const SERVICE_DEFAULT = ['/service', '/service/work-orders', '/service/measurements',
                         '/service/ra-bills', '/service/payments']

const MOBILE_TABS_BY_ROLE: Partial<Record<UserRole, string[]>> = {
  accounts_spoc: ['/service', '/service/ra-bills', '/service/contractor-payables',
                  '/service/payments', '/service/statutory'],
}

const SERVICE_FALLBACK = [...SERVICE_DEFAULT, '/service/variations',
  '/service/contractor-payables', '/service/advances', '/service/work-items', '/service/masters']

export function ServiceShell() {
  const { profile, site, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => { setOpen(false) }, [location.pathname])

  if (!profile) return null
  const role = profile.role
  const entries = NAV.filter((n) => canSeeServiceRoute(role, n.to))
  const groups = [...new Set(entries.map((e) => e.group))]
  const here = entries.find((e) => e.to === location.pathname)

  return (
    <div className="min-h-screen lg:flex">
      {/* ---------- Sidebar ---------- */}
      <aside className={clsx(
        // Dark graphite sidebar - branding 30-09-2026. The amber tint that used to
        // say "you are on the Service side" is retired with amber itself; the
        // module name and the SERVICE tag below say it instead.
        'no-print fixed inset-y-0 left-0 z-40 w-72 transform overflow-y-auto border-r border-sidebar-line bg-sidebar transition-transform lg:static lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full',
      )}>
        <div className="flex items-center gap-2 border-b border-sidebar-line px-4 py-3">
          {/* White OVAL mark on graphite - branding 10-10-2026, see AppShell. */}
          <span className="flex h-10 w-10 shrink-0 items-center justify-center">
            <img src="/logo-oval.png" alt="OVAL" className="h-9 w-9 object-contain brightness-0 invert" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 truncate text-sm font-bold text-white">
              Service Procurement
              <span className="rounded bg-oval-700 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-white">Service</span>
            </p>
            <p className="truncate text-[11px] text-sidebar-muted">Works contracts &amp; measurement</p>
          </div>
          <button className="rounded p-1 text-sidebar-muted hover:bg-sidebar-hover hover:text-white lg:hidden"
                  onClick={() => setOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-sidebar-line bg-sidebar-hover/60 px-4 py-3">
          <p className="truncate text-sm font-semibold text-white">{profile.full_name}</p>
          <p className="truncate text-xs text-sidebar-text">{ROLE_LABEL[role]}</p>
          <p className="mt-1 truncate text-[11px] text-sidebar-muted">
            {site ? `${site.site_name} (${site.site_code})` : profile.site_id ?? '—'}
          </p>
          {isReadOnly(role) && (
            <span className="pill-grey mt-2"><ShieldCheck className="h-3 w-3" /> View only</span>
          )}
        </div>

        <nav className="space-y-4 px-3 py-4">
          {groups.map((g) => (
            <div key={g}>
              <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-sidebar-muted">{g}</p>
              <div className="space-y-0.5">
                {entries.filter((e) => e.group === g).map((e) => (
                  <NavLink key={e.to} to={e.to} end={e.end}
                           className={({ isActive }) => clsx('nav-item', isActive && 'nav-item-active')}>
                    <e.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{e.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* ---------- Main ---------- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-20 flex items-center gap-1.5 border-b border-slate-200 bg-white/95 px-2 py-2.5 sm:gap-3 sm:px-4 backdrop-blur">
          <button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden"
                  onClick={() => setOpen(true)}>
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-800">
              {here?.label ?? 'Service Procurement'}
            </p>
          </div>
          <ModuleSwitch current="service" />
          <ChangePasswordButton />
          <button
            onClick={async () => { await signOut(); navigate('/login') }}
            title={`Sign out of ${profile.full_name}'s account`}
            className="flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
          <NotificationBell module="service" />
        </header>

        {/* A thin rule under the header, the width of the page. On a phone
            the sidebar is hidden, so this is the only permanent sign of which
            side you are on. Emerald since the 30-09-2026 branding (was amber);
            the Materials side has no rule. */}
        <div className="h-0.5 shrink-0 bg-gradient-to-r from-oval-400 via-oval-700 to-oval-400" />

        <main className="min-w-0 flex-1 px-4 py-4 sm:px-6 sm:py-6">
          <Outlet />
        </main>

        <footer className="no-print border-t border-slate-200 px-4 py-3 text-center text-[11px] text-slate-400 sm:px-6">
          <span className="inline-flex items-center gap-1.5">
            <ClipboardCheck className="h-3 w-3" />
            Service Procurement · every measurement is recorded by one person and certified by another
          </span>
          {/* Which build is on the server. See src/lib/build.ts. */}
          <span className="mt-0.5 block text-[10px] text-slate-300">{BUILD_LABEL}</span>
        </footer>

        <MobileTabSpacer />
        <MobileTabBar tabs={pickMobileTabs(
          MOBILE_TABS_BY_ROLE[role] ?? SERVICE_DEFAULT, SERVICE_FALLBACK, MOBILE_CATALOGUE,
          (to) => canSeeServiceRoute(role, to),
        )} />
      </div>
    </div>
  )
}
